import React, { useEffect, useRef, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { DatePicker, TimePicker, Input } from 'antd';
import moment from 'moment-timezone';
import { Button } from '@mui/material';
import { clsx } from 'clsx';

import { Credit, User } from '@types';
import { axiosErrorToString } from '@utils';
import { ReminderSetDto, ReminderUnsetDto, userHttp } from '@network';
import { useSettingsContext } from '@providers';
import { Loader } from '@components/Loader';
import cls from './ReminderComponent.module.css';

const { TextArea } = Input;

interface Props {
  user: User;
  credit?: Credit;
}

export const ReminderComponent = ({ user, credit }: Props) => {
  const notify = useNotify();
  const { refreshNotifications } = useSettingsContext();
  const [comment, setComment] = useState('');
  const [timeValue, setTimeValue] = useState<moment.Moment | null>(null);
  const [showReminder, setShowReminder] = useState<boolean>(false);
  const reminderDate = useRef<string>('');
  const reminderTime = useRef<string>('');
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState<boolean | undefined>();
  const isActiveLoading = useRef<boolean>(false);

  const loadStatus = async () => {
    if (isActive !== undefined || isActiveLoading.current) return;
    isActiveLoading.current = true;
    const status = await userHttp.reminderStatus({ userId: user.id, creditId: credit?.id });
    isActiveLoading.current = false;
    setIsActive(status);
  };

  useEffect(() => void loadStatus(), [user.id, credit?.id]);

  const resetReminder = async () => {
    const data: ReminderUnsetDto = { userId: user.id };

    if (credit) {
      data.creditId = credit.id;
    }

    setLoading(true);

    try {
      const status = await userHttp.reminderUnset(data);
      setIsActive(status);
    } catch (e) {
      console.error(e);
      notify(axiosErrorToString(e));
    } finally {
      setLoading(false);
      refreshNotifications();
    }
  };

  const saveReminder = async () => {
    if (!reminderDate.current) {
      notify('Please, set Reminder date', { type: 'error' });
      return;
    }

    if (!reminderTime.current) {
      notify('Please, set Reminder time', { type: 'error' });
      return;
    }

    if (!comment.trim()) {
      notify('Please, set Reminder comment', { type: 'error' });
      return;
    }

    const data: ReminderSetDto = {
      userId: user.id,
      comment,
      contactNextCycle: moment.tz(`${reminderDate.current} ${reminderTime.current}`,
        'YYYY-MM-DD HH:mm', 'America/Mexico_City').utc().format(),
    };

    if (credit) data.creditId = credit.id;

    setLoading(true);

    try {
      const status = await userHttp.reminderSet(data);
      setIsActive(status);
    } catch (e) {
      console.log(e);
      notify(axiosErrorToString(e));
    } finally {
      setLoading(false);
      refreshNotifications();
    }

    setShowReminder(false);
    reminderDate.current = '';
    reminderTime.current = '';
    setComment('');
  };

  const addComment = async (value: string) => {
    if (!value || !value.trim().length) return;

    if (value.length <= 100) {
      setComment(value);
    }
  };

  const selectReminderDate = (date: any) => {
    reminderDate.current = date.format('YYYY-MM-DD');
  };

  const setNowMexicoTime = () => {
    const nowMexico = moment.tz('America/Mexico_City');

    setTimeValue(nowMexico);
    reminderTime.current = nowMexico.format('HH:mm');
  };

  if (isActive === undefined) <span />;

  return (
    <div className={cls.reminderWrap}>
      {isActive ? (
        <Button
          variant="contained"
          className={clsx(cls.btn, cls.btnCancel)}
          onClick={resetReminder}
          disabled={loading}
        >{loading && <><Loader mini />&nbsp;</>} Cancel Reminder</Button>
      ) : (
        <Button
          variant="contained"
          className={clsx(cls.btn, cls.btnSet)}
          onClick={() => setShowReminder(true)}
          disabled={loading}
        >{loading && <><Loader mini />&nbsp;</>} Set reminder</Button>
      )}

      {showReminder && (
        <div className={clsx(cls.reminder)}>
          <div className={cls.reminderTitle}>Set a Reminder</div>
          <div className={cls.reminderDate}>
            <div className={clsx(cls.field, cls.dateField)}>
              <span className={cls.fieldTitle}>Date</span>
              <DatePicker
                format="DD/MM/YYYY"
                placeholder="DD/MM/YYYY"
                onChange={selectReminderDate}
                className={cls.reminderDay}
                suffixIcon={null}
                getPopupContainer={trigger => trigger.parentElement ?? document.body}
                disabledDate={(current) =>
                  current.isBefore(
                    moment.tz('America/Mexico_City').startOf('day'),
                  )
                }
              />
            </div>
            <div className={clsx(cls.field, cls.timeField)}>
              <span className={cls.fieldTitle}>Time</span>
              <TimePicker
                value={timeValue}
                onChange={(date) => {
                  setTimeValue(date);
                  reminderTime.current = date?.format('HH:mm') ?? '';
                }}
                format="HH:mm"
                placeholder="00:00"
                className={cls.reminderTime}
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                suffixIcon={null}
                showNow={false}
                renderExtraFooter={() => (
                  <div className={cls.nowBtn} onClick={setNowMexicoTime}>Now</div>
                )}
              />
            </div>
          </div>
          <div className={cls.reminderMessage}>
            <TextArea
              className={cls.message}
              value={comment}
              onChange={e => addComment(e.target.value)}
              placeholder="Type your reminder" />
          </div>
          <div className={cls.reminderActions}>
            <Button
              className={clsx(cls.btnO, cls.btnOCancel)}
              onClick={() => setShowReminder(false)}
            >Cancel</Button>
            <Button
              className={clsx(cls.btnO, cls.btnOSet)}
              onClick={saveReminder}>Set</Button>
          </div>
        </div>
      )}
    </div>
  );
};
